import type { UserAuthIdentity } from '@/api/types/User'
import { startAuth } from '@/stores/authStore'
import { useMemo } from 'react'

type AuthProviderKey = 'google' | 'apple'

const AUTH_PROVIDERS: Record<AuthProviderKey, {
  label: string
  description: string
  provider: string[]
  startAuth?: () => void
}> = {
  google: {
    label: 'Google Account',
    description: 'Sign in with your Google account',
    provider: ['Google'],
    startAuth: () => startAuth('google', true),
  },
  apple: {
    label: 'Apple Account',
    description: 'Sign in with your Apple account',
    provider: ['SignInWithApple'],
    startAuth: () => startAuth('apple', true),
  },
}

export function useAuthIdentities(rawIdentities: UserAuthIdentity[] = []) {
  const identities = useMemo(() => {
    return (Object.keys(AUTH_PROVIDERS) as AuthProviderKey[])
      .map(key => {
        const providerConfig = AUTH_PROVIDERS[key]

        const identity = rawIdentities.find(i =>
          providerConfig.provider.includes(i.provider)
        )

        return {
          key,
          label: providerConfig.label,
          description: providerConfig.description,
          connected: Boolean(identity),
          primary: identity?.primary,
          identity,
          startAuth: identity ? undefined : providerConfig.startAuth,
        }
      })
  }, [rawIdentities])

  const hasEmailPasswordIdentity = useMemo(() => {
    return rawIdentities.some(i => i.provider === 'Cognito')
  }, [rawIdentities])

  const isEmailPasswordPrimary = useMemo(() => {
    const cognitoIdentity = rawIdentities.find(i => i.provider === 'Cognito')
    return cognitoIdentity?.primary || false
  }, [rawIdentities])

  return { identities, hasEmailPasswordIdentity, isEmailPasswordPrimary }
}
